/**
 * The review's findings as a plain-text list an operator can paste into an
 * email or memo for the attorney (AIVA-8750) — the whole review, or one group.
 *
 * Plain text on purpose: it pastes the same into Outlook, Word, Teams or a
 * phone, and goes through the same `writeText` → `execCommand` path every
 * other Copy button uses, which works in the Office hosts that block the
 * richer clipboard API. Capitalised headings and "•" bullets keep the
 * structure without any formatting.
 *
 * Pure, so the wording and order are testable without rendering anything.
 */
import { groupFindings, isApplied } from "./grouping";
import { GROUPS, sortOrderNote, type RuleGroupId } from "./ruleCatalog";
import type { Finding } from "./types";

/** The five categories attorneys ask for, in their order. Defined-terms rules
 *  not named here (missing_defined_term, and any added later) are
 *  Miscellaneous, so no finding is ever left off the list. */
const DEFINED_TERM_CATEGORIES: ReadonlyArray<{ readonly label: string; readonly rules: readonly string[] }> = [
  { label: "Defined but not used", rules: ["defined_not_used"] },
  { label: "Used but not defined", rules: ["used_not_defined"] },
  { label: "Used before defined", rules: ["used_before_defined"] },
  { label: "Out of alphabetical order", rules: ["alphabetical_order"] },
];
const MISCELLANEOUS = "Miscellaneous";

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** What the operator has done with it, when that is anything. */
function statusTag(finding: Finding): string {
  if (isApplied(finding)) return " [fixed]";
  if (finding.status === "skipped") return " [skipped]";
  if (finding.status === "stale") return " [couldn't be applied]";
  return "";
}

/** The finding's problem, plus how its acronyms were sorted when it has a
 *  sort-order note — the attorney needs to know an entry was ordered by its
 *  abbreviation because no full form was found (AIVA-8606). */
function bullet(finding: Finding): string {
  const text = [finding.problem || finding.comment, sortOrderNote(finding)]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return `  • ${text}${statusTag(finding)}`;
}

/** One heading and its findings. A defined-terms category with none still
 *  gets its heading, saying so, so the attorney can see it was checked. */
function section(label: string, findings: readonly Finding[]): string[] {
  if (findings.length === 0) return [`${label} (0) — none found`];
  return [`${label} (${findings.length})`, ...findings.map(bullet)];
}

function definedTermLines(findings: readonly Finding[]): string[] {
  const named = new Set(DEFINED_TERM_CATEGORIES.flatMap((c) => c.rules));
  return [
    ...DEFINED_TERM_CATEGORIES.flatMap((category) =>
      section(
        category.label,
        findings.filter((f) => category.rules.includes(f.rule_id)),
      ),
    ),
    ...section(
      MISCELLANEOUS,
      findings.filter((f) => !named.has(f.rule_id)),
    ),
  ];
}

/** The summary line: how many issues, and where they stand. */
function summary(findings: readonly Finding[]): string {
  const fixed = findings.filter(isApplied).length;
  const skipped = findings.filter((f) => f.status === "skipped").length;
  const open = findings.length - fixed - skipped;
  return `${plural(findings.length, "issue")} — ${open} open, ${fixed} fixed, ${skipped} skipped`;
}

/**
 * The list, for every group or just `groupId`. Groups and rules come in the
 * panel's own order (`groupFindings`), so the list reads the way the operator
 * sees the review; within the Defined terms group, findings are filed under
 * the attorney categories rather than the rule names. Every finding is
 * listed, decided ones tagged [fixed] or [skipped].
 */
export function findingsListText(findings: readonly Finding[], groupId?: RuleGroupId): string {
  const groups = groupFindings(findings).filter((g) => !groupId || g.id === groupId);
  const included = groups.flatMap((g) => g.rules.flatMap((r) => r.findings));
  const scope = groupId ? ` — ${GROUPS.find((g) => g.id === groupId)?.label ?? groupId}` : "";

  const lines = [`Document review${scope}`, summary(included)];
  for (const group of groups) {
    const groupFindingsList = group.rules.flatMap((r) => r.findings);
    lines.push("", `${group.label.toUpperCase()} (${groupFindingsList.length})`);
    if (group.id === "defined_term") lines.push(...definedTermLines(groupFindingsList));
    else for (const rule of group.rules) lines.push(...section(rule.name, rule.findings));
  }
  return lines.join("\n");
}
